import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ai } from '@repletoryreach/sdk-api';
import { Card, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { DataTable, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { useCredits } from '../../lib/credits';
import { formatDateTime, type Indexed } from '../../lib/format';
import { useT } from '../../i18n/I18nProvider';

const TONE: Record<string, 'healthy' | 'warning' | 'down' | 'pending'> = { done: 'healthy', held: 'pending', failed: 'down', refused: 'warning' };

/** History (14 October 2026): every AI call, from this page or an API key, with what it cost. A row opens it in full. */
export function HistoryTab() {
  const t = useT();
  const credits = useCredits();
  const q = useQuery({ queryKey: ['ai', 'requests'], queryFn: () => ai.listRequests(100) });
  const rows = (q.data?.data ?? []) as Indexed<ai.AiRequestRow>[];
  const [open, setOpen] = useState<string | null>(null);

  return (
    <Card withBorder>
      <Stack gap="md">
        <Stack gap={2}>
          <Title order={4}>{t('ai.history.title')}</Title>
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('ai.history.body')}</Text>
        </Stack>
        <DataTable<Indexed<ai.AiRequestRow>>
          records={rows}
          loading={q.isPending}
          idAccessor="id"
          emptyState={t('ai.history.empty')}
          onRowClick={(r) => setOpen(r.id)}
          columns={[
            { accessor: 'created_at', title: t('ai.history.when'), render: (r) => formatDateTime(r.created_at) },
            { accessor: 'tool', title: t('ai.history.tool'), render: (r) => r.tool === 'skill' && r.skill ? t('ai.history.skill', { skill: t(`ai.skill.${r.skill}.name`, r.skill) }) : t(`ai.tool.${r.tool}`, r.tool) },
            { accessor: 'model', title: t('ai.model.label') },
            { accessor: 'by_api_key', title: t('ai.history.by'), render: (r) => r.by_api_key ? t('ai.history.byKey', { name: r.api_key_name ?? '' }) : t('ai.history.byConsole') },
            { accessor: 'charged_minor', title: t('ai.history.cost'), textAlign: 'right', render: (r) => credits(r.charged_minor) },
            { accessor: 'status', title: t('common.status'), render: (r) => <StatusBadge status={TONE[r.status] ?? 'pending'}>{t(`ai.status.${r.status}`, r.status)}</StatusBadge> },
            { accessor: 'preview', title: t('ai.history.preview'), render: (r) => <Text role="caption" lineClamp={2}>{r.error ?? r.preview ?? ''}</Text> }
          ]}
        />
      </Stack>
      <RequestModal id={open} onClose={() => setOpen(null)} />
    </Card>
  );
}

/** One call in full: a transcription's whole text, or why it failed. */
function RequestModal({ id, onClose }: { id: string | null; onClose: () => void }) {
  const t = useT();
  const credits = useCredits();
  const q = useQuery({ queryKey: ['ai', 'request', id], queryFn: () => ai.getRequest(id!), enabled: !!id });
  const r = q.data?.data;
  return (
    <Modal opened={!!id} onClose={onClose} title={r ? t(`ai.tool.${r.tool}`, r.tool) : t('ai.history.title')} size="lg">
      {r && (
        <Stack gap="sm" data-testid="ai-request">
          <Text role="caption" c="var(--rr-color-text-muted)">
            {t('ai.history.detail', { when: formatDateTime(r.created_at), model: r.model, cost: credits(r.charged_minor), status: t(`ai.status.${r.status}`, r.status) })}
          </Text>
          {r.error && <Text role="bodySmall" c="var(--rr-color-error)">{r.error}</Text>}
          {(r.result ?? r.preview) && <div className="rr-ai__output rr-ai__transcript">{r.result ?? r.preview}</div>}
          {r.media_ids.length > 0 && <Text role="caption">{t('ai.history.saved', { count: r.media_ids.length })}</Text>}
        </Stack>
      )}
    </Modal>
  );
}
